import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

const LOCAL_PREFIX = 'user-pref:';

function loadLocal<T>(key: string, fallback: T): T {
    try {
        const raw = localStorage.getItem(LOCAL_PREFIX + key);
        if (raw) {
            const parsed = JSON.parse(raw) as T;
            // Shallow-merge so newly added fields fall back to defaults.
            if (parsed && typeof parsed === 'object' && fallback && typeof fallback === 'object') {
                return { ...(fallback as object), ...(parsed as object) } as T;
            }
            return parsed;
        }
    } catch { /* ignore */ }
    return fallback;
}

function saveLocal(key: string, value: unknown): void {
    try { localStorage.setItem(LOCAL_PREFIX + key, JSON.stringify(value)); } catch { /* ignore */ }
}

async function persistRemote(key: string, value: unknown): Promise<void> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return;
    await supabase
        .from('user_settings')
        .upsert(
            {
                user_id: session.user.id,
                key,
                value: value as never,
                updated_at: new Date().toISOString(),
            },
            { onConflict: 'user_id,key' },
        );
}

/** ONE fetch for ALL of the signed-in user's settings rows. Dashboard boot
 *  used to run a per-key select for every mounted useUserPref (~5×); every
 *  key now reads from this shared map. No session ⇒ empty map (local-only). */
const USER_SETTINGS_KEY = ['user-settings-all'] as const;

async function fetchAllUserSettings(): Promise<Record<string, unknown>> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return {};
    const { data } = await supabase
        .from('user_settings')
        .select('key, value')
        .eq('user_id', session.user.id);
    const map: Record<string, unknown> = {};
    for (const row of (data ?? []) as { key: string; value: unknown }[]) map[row.key] = row.value;
    return map;
}

type Setter<T> = (next: T | ((prev: T) => T)) => void;

/**
 * Per-user remembered preference. Reads from localStorage for instant
 * first paint, then upgrades from the DB (`user_settings` table) so the
 * same value follows the signed-in user across devices.
 *
 * The third tuple element, `loaded`, flips true once the authoritative DB
 * lookup has resolved (or there's no session). Callers that act on the value
 * exactly once — e.g. the dashboard's "land on my default tab" redirect —
 * must wait for `loaded` so they don't fire against the stale localStorage
 * fallback before the real value arrives.
 */
export function useUserPref<T>(key: string, defaultValue: T): [T, Setter<T>, boolean] {
    const queryClient = useQueryClient();
    const query = useQuery({
        queryKey: USER_SETTINGS_KEY,
        staleTime: 60_000,
        queryFn: fetchAllUserSettings,
    });

    const remote = query.data ? query.data[key] : undefined;
    const value = useMemo<T>(() => {
        if (remote == null) return loadLocal(key, defaultValue);
        // Shallow-merge with defaults to absorb new fields safely.
        return (remote && typeof remote === 'object' && defaultValue && typeof defaultValue === 'object')
            ? { ...(defaultValue as object), ...(remote as object) } as T
            : (remote as T);
        // defaultValue is intentionally not in deps — only the key matters
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [remote, key]);
    const valueRef = useRef(value);
    valueRef.current = value;

    // Mirror the authoritative remote value into localStorage (matches the
    // old hook's behavior so the next cold paint starts from the DB value).
    useEffect(() => {
        if (remote != null) saveLocal(key, value);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [remote, key]);

    const update: Setter<T> = useCallback((next) => {
        const resolved = typeof next === 'function'
            ? (next as (p: T) => T)(valueRef.current)
            : next;
        // Patch the shared cache so every instance of this key updates
        // instantly, keep the local mirror, then persist to the DB.
        queryClient.setQueryData<Record<string, unknown>>(USER_SETTINGS_KEY, (old) => ({ ...(old ?? {}), [key]: resolved }));
        saveLocal(key, resolved);
        void persistRemote(key, resolved);
    }, [key, queryClient]);

    return [value, update, query.isSuccess];
}
